// Login hardening (2026-09-30): Cloudflare Turnstile (action 'login') for the password form, shown only after the
// server answered captchaRequired. Loader pattern of client-lamirage-in CallNowSheet. Any failure to load or run the
// widget (script blocked, domain not authorised 110xxx, network) calls onUnavailable(): the server then accepts one
// tokenless attempt per minute per IP, so a broken widget can never lock anyone out.
import { useEffect, useRef } from "react";
import { loginTurnstileSiteKey } from "@/lib/loginCaptcha";

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

type TurnstileApi = { render: (el: HTMLElement, opts: Record<string, unknown>) => string; remove: (id: string) => void; reset: (id: string) => void };
const ts = () => (window as unknown as { turnstile?: TurnstileApi }).turnstile;

let scriptPromise: Promise<void> | null = null;
function loadTurnstile(): Promise<void> {
  if (ts()) return Promise.resolve();
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = SCRIPT_SRC; s.async = true; s.defer = true;
      s.onload = () => resolve();
      s.onerror = () => { scriptPromise = null; reject(new Error('turnstile script failed')); };
      document.head.appendChild(s);
    });
  }
  return scriptPromise;
}

export function LoginTurnstile({ onToken, onUnavailable, resetKey }: {
  onToken: (token: string | null) => void;
  onUnavailable: () => void;
  /** Changes after every login attempt: tokens are single-use, so the widget is reset for a fresh one. */
  resetKey: number;
}) {
  const box = useRef<HTMLDivElement>(null);
  const id = useRef<string | null>(null);
  const cb = useRef({ onToken, onUnavailable });
  cb.current = { onToken, onUnavailable };

  useEffect(() => {
    let cancelled = false;
    loadTurnstile().then(() => {
      const api = ts();
      if (cancelled || !box.current) return;
      if (!api) { cb.current.onUnavailable(); return; }
      try {
        id.current = api.render(box.current, {
          sitekey: loginTurnstileSiteKey(), action: 'login', theme: 'dark',
          callback: (t: string) => cb.current.onToken(t),
          'expired-callback': () => cb.current.onToken(null),
          'error-callback': () => { cb.current.onToken(null); cb.current.onUnavailable(); return true; },
        });
      } catch { cb.current.onUnavailable(); }
    }).catch(() => { if (!cancelled) cb.current.onUnavailable(); });
    return () => {
      cancelled = true;
      const api = ts();
      if (id.current && api) { try { api.remove(id.current); } catch { /* already gone */ } }
      id.current = null;
    };
  }, []);

  useEffect(() => {
    if (resetKey === 0) return;
    const api = ts();
    if (id.current && api) { try { api.reset(id.current); } catch { /* re-rendered on next mount */ } }
  }, [resetKey]);

  return <div ref={box} className="flex justify-center min-h-[65px]" />;
}
